import React, { type JSX } from 'react';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { Divider, FormControlLabel, FormGroup, Switch } from '@mui/material';
import { setAllVisibleColumns, setVisibleColumns } from '@fiji/common/src/features/component/dataGridSlice';
import { CustomTransComponent } from 'components/CustomTransComponent';

// Columns forced to stay visible when toggling "show all".
const SHOW_ALL_FORCED_COLUMNS = ['actions', 'custom', '__check__', 'deviceName'];
// Structural columns that are never listed as user-configurable.
const NON_CONFIGURABLE_COLUMNS = ['actions', 'custom', '__check__'];

const areAllColumnsVisible = (visibleColumns: Record<string, boolean>): boolean =>
    Object.values(visibleColumns).every((value) => value === true);

const buildShowAllVisibility = (visibleColumns: Record<string, boolean>, checked: boolean): Record<string, boolean> => {
    const next: Record<string, boolean> = {};
    Object.keys(visibleColumns).forEach((field) => {
        next[field] = SHOW_ALL_FORCED_COLUMNS.includes(field) ? true : checked;
    });
    return next;
};

const isConfigurableColumn = (field?: string): boolean =>
    Boolean(field) && !NON_CONFIGURABLE_COLUMNS.includes(field as string);

const getSwitchSx = (theme: any): any => {
    const primary = theme?.palette?.primary;
    const main = primary?.main;
    return {
        '& .MuiSwitch-switchBase.Mui-checked': {
            color: main,
            '& + .MuiSwitch-track': { backgroundColor: main },
        },
        '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled': {
            color: primary?.[50],
            '& + .MuiSwitch-track': { backgroundColor: primary?.[200] },
        },
    };
};

export const ColumnVisibilityMenu = ({ columns }: any): JSX.Element => {
    const configurablePayload = useTypedSelector((state) => state.dataGrid?.configurablePayload);
    const dispatch = useAppDispatch();
    const theme: any = useTheme();
    const switchSx = getSwitchSx(theme);
    const visibleColumns = configurablePayload?.visibleColumns;

    return (
        <FormGroup sx={{ m: '0', minHeight: '500px', width: '100%' }}>
            <FormControlLabel
                sx={{ m: '0', padding: '8px' }}
                key={'showAll'}
                control={
                    <Switch
                        checked={areAllColumnsVisible(visibleColumns)}
                        onChange={(e): void => {
                            dispatch(setAllVisibleColumns(buildShowAllVisibility(visibleColumns, e.target.checked)));
                        }}
                        sx={switchSx}
                    />
                }
                label={<CustomTransComponent translationKey={'COMMON:SHOW_ALL'} />}
            />
            <Divider />
            {columns?.map(
                (column: any) =>
                    isConfigurableColumn(column?.field) && (
                        <FormControlLabel
                            sx={{ m: '0', padding: '8px' }}
                            key={column.field}
                            control={
                                <Switch
                                    id={column.field}
                                    disabled={column.hideable === false}
                                    checked={visibleColumns?.[column.field] ?? false}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                                        dispatch(setVisibleColumns({ key: e.target.id, value: e.target.checked }));
                                    }}
                                    sx={switchSx}
                                />
                            }
                            label={column.headerName}
                        />
                    )
            )}
        </FormGroup>
    );
};
